import { Fragment } from 'react';
import { chartPalette } from '../../design-system/tokens';
import './Heatmap.css';

export interface HeatmapCell {
  x: string;
  y: string;
  value: number | null;
}

/**
 * A heatmap, drawn by hand.
 *
 * One hue, light to dark — never a rainbow: magnitude is one quantity, so it
 * gets one ramp. The hue is slot 1 of the chart palette, so a heatmap belongs
 * to the same family as every other chart in the product.
 *
 * A cell with no value is drawn as absence — an outline, not the bottom of the
 * scale, which would read as "zero".
 */
export function Heatmap({
  cells,
  xLabel,
  yLabel,
  valueLabel,
}: {
  cells: HeatmapCell[];
  xLabel: string;
  yLabel: string;
  valueLabel: string;
}) {
  if (!cells.length) return <p className="sc-heatmap__empty">No rows matched.</p>;

  // Sorted, not first-seen: the rows arrive ordered by the measure, and a grid
  // whose labels jump around is one nobody can find anything in.
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const xs = [...new Set(cells.map((cell) => cell.x))].sort(collator.compare);
  const ys = [...new Set(cells.map((cell) => cell.y))].sort(collator.compare);
  const values = cells.map((cell) => cell.value).filter((value): value is number => value !== null);
  const highest = values.length ? Math.max(...values) : 1;
  const lowest = values.length ? Math.min(...values) : 0;
  const span = highest - lowest || 1;

  const byKey = new Map(cells.map((cell) => [`${cell.x}|${cell.y}`, cell.value]));

  return (
    <div
      className="sc-heatmap"
      role="img"
      aria-label={`${valueLabel} by ${xLabel} and ${yLabel}. The table below carries the same numbers.`}
    >
      <div
        className="sc-heatmap__grid"
        style={{ gridTemplateColumns: `minmax(60px, auto) repeat(${xs.length}, 1fr)` }}
      >
        <span className="sc-heatmap__corner" aria-hidden="true" />
        {xs.map((x) => (
          <span key={`head-${x}`} className="sc-heatmap__head">
            {x}
          </span>
        ))}

        {ys.map((y) => (
          <Fragment key={`row-${y}`}>
            <span className="sc-heatmap__row">{y}</span>
            {xs.map((x) => {
              const value = byKey.get(`${x}|${y}`) ?? null;
              const share = value === null ? 0 : (value - lowest) / span;
              return (
                <span
                  key={`${x}-${y}`}
                  className={value === null ? 'sc-heatmap__cell is-empty' : 'sc-heatmap__cell'}
                  style={
                    value === null
                      ? undefined
                      : { backgroundColor: chartPalette[0], opacity: 0.15 + share * 0.85 }
                  }
                  title={`${x} · ${y}: ${value === null ? 'no value' : value.toLocaleString()}`}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
      <p className="sc-heatmap__scale">
        <span>{lowest.toLocaleString()}</span>
        <span className="sc-heatmap__ramp" aria-hidden="true" />
        <span>{highest.toLocaleString()}</span>
      </p>
    </div>
  );
}
